import { FC, useMemo, useState } from 'react'
import { Box, ButtonBase, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ArrowForward, ArticleOutlined, CalendarMonthOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { usePendingBookingAcceptanceContracts } from 'services/contracts/hooks/useLeaseContractsBookingAgreement'
import {
  CONTRACT_ACTIVE_GROUP,
  CONTRACT_BOOKING_GROUP,
  CONTRACT_ENDED_GROUP,
  CONTRACT_STATUS
} from 'services/contracts/contracts.consts'
import { getLeaseStatusMeta } from './leases.utils'

const getContractPath = (id: number, leaseStatus?: number, isPendingAcceptance = false) =>
  leaseStatus === CONTRACT_STATUS.contractStatusBookingAgreement || isPendingAcceptance
    ? `/additional-services/${id}/booking-agreement`
    : `/additional-services/${id}`

// Lease contracts list ("Panel Klienta 3.0"): filter tabs + full-width contract cards.
const AdditionalServicesLease: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [leaseType, setLeaseType] = useState<number[]>(CONTRACT_ACTIVE_GROUP)
  const { leaseContractsList, isFetching, isLoading } = useLeaseContractsList()
  const { pendingContracts, isLoading: isPendingLoading } = usePendingBookingAcceptanceContracts()

  const isSignatureTab = leaseType === CONTRACT_BOOKING_GROUP

  const pendingContractIds = useMemo(
    () => new Set(pendingContracts.map(({ id }) => id)),
    [pendingContracts]
  )

  const filters = [
    {
      value: CONTRACT_ACTIVE_GROUP,
      label: formatMessage({ id: 'additionalServicesLease.active', defaultMessage: 'Aktywne' })
    },
    {
      value: CONTRACT_BOOKING_GROUP,
      label: formatMessage({ id: 'additionalServicesLease.signature', defaultMessage: 'Do podpisu' })
    },
    {
      value: CONTRACT_ENDED_GROUP,
      label: formatMessage({ id: 'additionalServicesLease.end', defaultMessage: 'Zakończone' })
    }
  ]

  const leaseContractsFiltredList = useMemo(() => {
    const byStatus = leaseContractsList.filter(({ leaseStatus }) => leaseType.includes(leaseStatus))
    return isSignatureTab ? [...pendingContracts, ...byStatus] : byStatus
  }, [leaseType, leaseContractsList, pendingContracts, isSignatureTab])

  const formatDates = (start: string, end: string, effectiveEnd?: string | null) =>
    `${dayjs(start).format('DD.MM.YYYY')} — ${dayjs(effectiveEnd || end).format('DD.MM.YYYY')}`

  const isEmptyVisible =
    !leaseContractsFiltredList.length && !isFetching && !(isSignatureTab && isPendingLoading)

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          borderBottom: `1px solid ${theme.r4r.greyLight}`,
          pb: '17px',
          pt: isDesktop ? 0 : '16px'
        }}>
        {filters.map(({ value, label }) => {
          const isSelected = value === leaseType

          return (
            <ButtonBase
              key={label}
              onClick={() => setLeaseType(value)}
              sx={{
                padding: '9px 18px',
                fontFamily: theme.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                border: `1px solid ${isSelected ? theme.r4r.navy : theme.r4r.greyLight}`,
                backgroundColor: isSelected ? theme.r4r.navy : '#fff',
                color: isSelected ? '#fff' : theme.r4r.grey,
                marginLeft: '-1px',
                ':first-of-type': { marginLeft: 0 }
              }}>
              {label}
            </ButtonBase>
          )
        })}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            pt: '20px',
            pb: '32px'
          }}>
          {isEmptyVisible && (
            <HelpdeskEmptyState
              Icon={ArticleOutlined}
              title={formatMessage({
                id: 'additionalServicesLease.noContracts',
                defaultMessage: 'Nie znaleźono umów dla wybranego typu.'
              })}
            />
          )}
          {leaseContractsFiltredList.map(
            ({ contractNumber, id, leaseStatus, leaseStart, leaseEnd, effectiveLeaseEnd }) => {
              const statusMeta = getLeaseStatusMeta(leaseStatus)
              const isPendingAcceptance = pendingContractIds.has(id)

              return (
                <ButtonBase
                  key={`${contractNumber}_${id}`}
                  onClick={() => navigate(getContractPath(id, leaseStatus, isPendingAcceptance))}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    textAlign: 'left',
                    gap: '12px',
                    width: '100%',
                    padding: '21px 23px',
                    backgroundColor: '#fff',
                    border: `1px solid ${theme.r4r.greyLight}`,
                    ':hover': { backgroundColor: '#F9FAFB' }
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: theme.r4r.navy,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {contractNumber}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: statusMeta.colors.backgroundColor,
                        padding: '3px 10px',
                        flexShrink: 0
                      }}>
                      <Typography
                        sx={{
                          color: statusMeta.colors.color,
                          fontSize: '10px',
                          fontWeight: 700,
                          lineHeight: 'normal',
                          whiteSpace: 'nowrap'
                        }}>
                        {formatMessage(statusMeta.i18n)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <CalendarMonthOutlined sx={{ fontSize: '14px', color: theme.r4r.grey }} />
                    <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
                      {formatDates(leaseStart, leaseEnd, effectiveLeaseEnd)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pt: '15px',
                      borderTop: `1px solid ${theme.r4r.greyLight}`
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.r4r.azure }}>
                        {formatMessage({
                          id: 'additionalServicesLease.details',
                          defaultMessage: 'Szczegóły'
                        })}
                      </Typography>
                      <ArrowForward sx={{ fontSize: '16px', color: theme.r4r.azure }} />
                    </Box>
                  </Box>
                </ButtonBase>
              )
            }
          )}
        </Box>
      )}
    </Box>
  )
}

export default AdditionalServicesLease
